// CR075-P1: Create Batch Run Wizard with Flyer Upload + Merchant Source Mapping
// CR101: Added Rule Packs selection step
// CR131: Load execution defaults from ops_settings
// CR168: Integrated FlyerInputModeSelector for Bucket discovery + Upload modes
// CR450: Merchants-First + Source Toggles w/ Flyer Capture Type, OCR Engine, Web Crawler
import { useState, useEffect, useRef, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Loader2, FileText, Globe, ChevronRight, ChevronLeft, Check,
  Upload, Trash2, AlertCircle, BookmarkCheck
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { RulePacksSection, RulePackSelection } from "./RulePacksSection";
import { useOpsSettings, DEFAULT_BATCH_EXECUTION } from "@/hooks/useOpsSettings";
import { FlyerInputModeSelector } from "@/components/admin/flyer-tools/FlyerInputModeSelector";
import type { FlyerInputMode, DiscoveredFlyer } from "@/lib/flyer-bucket/types";

// CR404: Merchant type from v_active_merchants view
interface Merchant {
  merchant_uuid: string;  // CR404: Canonical identifier
  merchant_key: string;
  display_name: string;
}

interface MerchantScanHealthRow {
  merchant_key: string | null;
  active_flyer_sources_count: number | null;
}

// CR229 → CR300: Array-based scan type filtering
type ScanType = "FLYER" | "WEBSITE";

interface PromptPack {
  id: string;
  name: string;
  version: number;
  is_active: boolean;
  scan_type: ScanType; // Legacy
  supported_scan_types: ScanType[]; // CR300: Array of supported types
  pack_kind: string; // CR467: SCAN | WORKFLOW
}

/** CR467: Check if a SCAN pack supports a given scan type. WORKFLOW packs always return false. */
const packSupports = (pack: PromptPack, type: ScanType): boolean =>
  pack.pack_kind === 'SCAN' && (pack.supported_scan_types?.includes(type) ?? pack.scan_type === type);

interface MerchantSource {
  id: string;
  merchant_id: string;
  merchant_name: string | null;
  source_url: string;
  source_label: string;
  source_type: string | null;
  is_active: boolean;
  status: string;
}

interface UploadedFlyer {
  id: string;
  file: File;
  filename: string;
  size: number;
  uploadedAt: Date;
  status: "READY" | "UPLOADING" | "FAILED" | "DELETED";
  merchantSourceId: string | null;
  merchantKey: string | null;
  error?: string;
  storagePath?: string;
}

interface CreateBatchRunWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (runId: string) => void;
}

// CR450: Wizard - Merchants first, conditional flyer upload step
// Order: scope (merchants) → sources → flyers-config (conditional: Upload only) → review
type WizardStep = "scope" | "sources" | "flyers-config" | "review";

// CR450: Flyer capture type options
type FlyerCaptureType = "UPLOAD" | "DOWNLOAD" | "IMAGE_CAPTURE";

const FLYER_CAPTURE_TYPE_LABELS: Record<FlyerCaptureType, string> = {
  UPLOAD: "Upload",
  DOWNLOAD: "Download",
  IMAGE_CAPTURE: "Image capture",
};

// CR450: Web crawler options matching S0 WebScanTab
const WEB_CRAWLER_OPTIONS = [
  { value: "firecrawl", label: "Firecrawl" },
  { value: "playwright", label: "Playwright (stub)" },
] as const;

type WebCrawlerOption = typeof WEB_CRAWLER_OPTIONS[number]["value"];

// CR450: OCR engine options matching PRIMARY_OCR_ENGINES in s0-settings/types.ts
const WIZARD_OCR_ENGINES = [
  { value: "paddle_ocr", label: "PaddleOCR (Vision AI)" },
  { value: "tesseract", label: "Tesseract (Browser)" },
  { value: "ai_vision", label: "Google Vision" },
  { value: "none", label: "None" },
] as const;

type WizardOcrEngine = typeof WIZARD_OCR_ENGINES[number]["value"];

export function CreateBatchRunWizard({ open, onOpenChange, onCreated }: CreateBatchRunWizardProps) {
  const { toast } = useToast();
  // CR450: Start on scope (merchants) step — merchants first
  const [step, setStep] = useState<WizardStep>("scope");
  const [creating, setCreating] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // CR131: Load global defaults from ops_settings
  const { value: opsDefaults, isLoading: loadingOpsDefaults } = useOpsSettings({ autoLoad: true });
  const [defaultsApplied, setDefaultsApplied] = useState(false);

  // Scope state
  const [merchants, setMerchants] = useState<Merchant[]>([]);
  const [flyerSupportedMerchantKeys, setFlyerSupportedMerchantKeys] = useState<Set<string>>(new Set());
  const [selectedMerchants, setSelectedMerchants] = useState<Set<string>>(new Set());
  const [loadingMerchants, setLoadingMerchants] = useState(true);
  const [selectAll, setSelectAll] = useState(true);

  // CR551: Remember last selected merchants (localStorage)
  const LS_REMEMBER_KEY = "batchWizard.rememberMerchantsEnabled";
  const LS_SAVED_KEY = "batchWizard.lastSelectedMerchants";
  const [rememberMerchants, setRememberMerchants] = useState<boolean>(() => {
    try { return localStorage.getItem(LS_REMEMBER_KEY) === "true"; } catch { return false; }
  });

  // CR551: Persist remembered selection to localStorage
  const persistSelection = useCallback((ids: Set<string>) => {
    try { localStorage.setItem(LS_SAVED_KEY, JSON.stringify([...ids])); } catch { /* noop */ }
  }, []);

  const clearSavedSelection = useCallback(() => {
    try { localStorage.removeItem(LS_SAVED_KEY); } catch { /* noop */ }
  }, []);

  const loadSavedSelection = useCallback((): string[] | null => {
    try {
      const raw = localStorage.getItem(LS_SAVED_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : null;
    } catch { return null; }
  }, []);

  // Sources state - CR094: Flyer enabled by default
  const [enableFlyers, setEnableFlyers] = useState(true);
  const [enableWebsite, setEnableWebsite] = useState(true);
  // CR450: Forum Signals removed entirely
  // CR552: S1 auto-run mode seeding
  const [s1AutoRun, setS1AutoRun] = useState(true);
  const [batchAutoRunPhase1, setBatchAutoRunPhase1] = useState(false);

  // CR450: New source config state
  const [flyerCaptureType, setFlyerCaptureType] = useState<FlyerCaptureType>("UPLOAD");
  const [ocrEngine, setOcrEngine] = useState<WizardOcrEngine>("paddle_ocr");
  const [webCrawler, setWebCrawler] = useState<WebCrawlerOption>("firecrawl");

  // Flyers config state
  const [uploadedFlyers, setUploadedFlyers] = useState<UploadedFlyer[]>([]);
  const [flyerSources, setFlyerSources] = useState<MerchantSource[]>([]);
  const [loadingFlyerSources, setLoadingFlyerSources] = useState(false);
  const [autoDeleteAfterExtraction, setAutoDeleteAfterExtraction] = useState(true);

  // CR168: Bucket discovery state
  const [flyerInputMode, setFlyerInputMode] = useState<FlyerInputMode>("bucket");
  const [bucketSourceId, setBucketSourceId] = useState<string | null>(null);
  const [selectedBucketFlyers, setSelectedBucketFlyers] = useState<DiscoveredFlyer[]>([]);

  // Website config state - CR131: Defaults now loaded from ops_settings
  const [promptPacks, setPromptPacks] = useState<PromptPack[]>([]);
  // CR229: Separate pack IDs for Flyer vs Website
  const [flyerPackId, setFlyerPackId] = useState<string | null>(null);
  const [websitePackId, setWebsitePackId] = useState<string | null>(null);
  const [loadingPacks, setLoadingPacks] = useState(false);
  const [loadPacksError, setLoadPacksError] = useState<string | null>(null);
  const [concurrentEnabled, setConcurrentEnabled] = useState(DEFAULT_BATCH_EXECUTION.max_concurrent_scans > 0);
  const [maxConcurrent, setMaxConcurrent] = useState(DEFAULT_BATCH_EXECUTION.max_concurrent_scans);
  const [timeoutSeconds, setTimeoutSeconds] = useState(DEFAULT_BATCH_EXECUTION.timeout_seconds);

  // CR101: Rule packs state
  const [rulePackSelection, setRulePackSelection] = useState<RulePackSelection>({
    normalizePackId: null,
    normalizePackSource: "active",
    dedupePackId: null,
    dedupePackSource: "active",
  });

  // CR131: Apply ops_settings defaults when loaded
  useEffect(() => {
    if (!loadingOpsDefaults && !defaultsApplied && opsDefaults) {
      setConcurrentEnabled(opsDefaults.max_concurrent_scans > 0);
      setMaxConcurrent(opsDefaults.max_concurrent_scans);
      setTimeoutSeconds(opsDefaults.timeout_seconds);
      setBatchAutoRunPhase1(Boolean(opsDefaults.auto_collection && opsDefaults.auto_processing));
      setDefaultsApplied(true);
    }
  }, [loadingOpsDefaults, defaultsApplied, opsDefaults]);

  // Reset defaults applied flag when dialog opens
  useEffect(() => {
    if (open) {
      setDefaultsApplied(false);
    }
  }, [open]);

  // Fetch merchants
  useEffect(() => {
    if (open) {
      fetchMerchants();
    }
  }, [open]);

  // CR404: Fetch from v_active_merchants view (Active + scan_enabled + not hidden)
  const fetchMerchants = async () => {
    setLoadingMerchants(true);
    try {
      const [merchantResult, scanHealthResult] = await Promise.all([
        supabase
          .from("v_active_merchants")
          .select("merchant_uuid, merchant_key, display_name")
          .order("display_name"),
        supabase
          .from("v_merchants_scan_health")
          .select("merchant_key, active_flyer_sources_count"),
      ]);

      const { data, error } = merchantResult;

      if (error) throw error;
      const merchantList = (data || []) as Merchant[];
      setMerchants(merchantList);

      // Non-blocking enrichment: show asterisk for merchants with at least 1 active flyer source
      if (scanHealthResult.error) {
        console.error("Failed to fetch merchant scan health:", scanHealthResult.error);
        setFlyerSupportedMerchantKeys(new Set());
      } else {
        const supportedKeys = new Set(
          ((scanHealthResult.data || []) as MerchantScanHealthRow[])
            .filter((row) => (row.active_flyer_sources_count || 0) > 0 && typeof row.merchant_key === "string")
            .map((row) => row.merchant_key as string)
        );
        setFlyerSupportedMerchantKeys(supportedKeys);
      }

      // CR551: If remember is ON, restore saved selection intersected with available merchants
      const availableIds = new Set(merchantList.map((m) => m.merchant_uuid));
      if (rememberMerchants) {
        const saved = loadSavedSelection();
        if (saved && saved.length > 0) {
          const restored = new Set(saved.filter((id) => availableIds.has(id)));
          if (restored.size > 0) {
            setSelectedMerchants(restored);
            setSelectAll(restored.size === merchantList.length);
            return; // skip default select-all
          }
        }
      }

      // CR404: Select all by default using UUID as canonical identifier
      setSelectedMerchants(new Set(merchantList.map((m) => m.merchant_uuid)));
      setSelectAll(true);
    } catch (err) {
      console.error("Failed to fetch merchants:", err);
      toast({
        title: "Error",
        description: "Failed to load Active merchants",
        variant: "destructive",
      });
    } finally {
      setLoadingMerchants(false);
    }
  };

  // Fetch flyer sources (Official Flyer type, active)
  const fetchFlyerSources = useCallback(async () => {
    setLoadingFlyerSources(true);
    try {
      const { data, error } = await supabase
        .from("merchant_sources")
        .select("id, merchant_id, merchant_name, source_url, source_label, source_type, is_active, status")
        .eq("source_type", "official_flyer")
        .eq("is_active", true)
        .order("merchant_name", { ascending: true });

      if (error) throw error;
      setFlyerSources((data || []) as MerchantSource[]);
    } catch (err) {
      console.error("Failed to fetch flyer sources:", err);
    } finally {
      setLoadingFlyerSources(false);
    }
  }, []);

  // Fetch flyer sources when entering flyers config step
  useEffect(() => {
    if (step === "flyers-config" && enableFlyers) {
      fetchFlyerSources();
    }
  }, [step, enableFlyers, fetchFlyerSources]);

  // CR094: Fetch prompt packs when entering sources step (now required there)
  useEffect(() => {
    if (step === "sources" && (enableFlyers || enableWebsite)) {
      fetchPromptPacks();
    }
  }, [step, enableFlyers, enableWebsite]);

  const fetchPromptPacks = async () => {
    setLoadingPacks(true);
    setLoadPacksError(null);
    try {
      const { data, error } = await supabase
        .from("prompt_packs")
        .select("id, name, version, is_active, scan_type, supported_scan_types, pack_kind") // CR300+CR467
        .eq("domain", "gift_card_deals")
        .eq("pack_kind", "SCAN") // CR467: Only show SCAN packs in batch wizard
        .is("deleted_at", null)
        .order("updated_at", { ascending: false });

      if (error) throw error;
      // Cast supported_scan_types from string[] to ScanType[]
      const packs = (data || []).map(p => ({
        ...p,
        supported_scan_types: (p.supported_scan_types || []) as ScanType[],
        pack_kind: (p.pack_kind || 'SCAN') as string,
      })) as PromptPack[];
      setPromptPacks(packs);

      // CR300: Default selection by supported_scan_types array
      const flyerPacks = packs.filter(p => packSupports(p, "FLYER"));
      const websitePacks = packs.filter(p => packSupports(p, "WEBSITE"));

      if (!flyerPackId && flyerPacks.length > 0) {
        const activeFlyerPack = flyerPacks.find((p) => p.is_active);
        setFlyerPackId(activeFlyerPack?.id || flyerPacks[0].id);
      }
      if (!websitePackId && websitePacks.length > 0) {
        const activeWebsitePack = websitePacks.find((p) => p.is_active);
        setWebsitePackId(activeWebsitePack?.id || websitePacks[0].id);
      }
    } catch (err) {
      console.error("Failed to fetch prompt packs:", err);
      setLoadPacksError("Failed to load prompt packs");
    } finally {
      setLoadingPacks(false);
    }
  };

  // CR404: Selection uses merchant_uuid as canonical identifier
  const handleSelectAll = (checked: boolean) => {
    setSelectAll(checked);
    const newSet = checked ? new Set(merchants.map((m) => m.merchant_uuid)) : new Set<string>();
    setSelectedMerchants(newSet);
    if (rememberMerchants) persistSelection(newSet);
  };

  // CR404: Toggle by UUID
  const handleMerchantToggle = (merchantUuid: string) => {
    const newSelected = new Set(selectedMerchants);
    if (newSelected.has(merchantUuid)) {
      newSelected.delete(merchantUuid);
    } else {
      newSelected.add(merchantUuid);
    }
    setSelectedMerchants(newSelected);
    setSelectAll(newSelected.size === merchants.length);
    if (rememberMerchants) persistSelection(newSelected);
  };

  // CR551: Toggle remember merchants
  const handleRememberToggle = (enabled: boolean) => {
    setRememberMerchants(enabled);
    try { localStorage.setItem(LS_REMEMBER_KEY, String(enabled)); } catch { /* noop */ }
    if (enabled) {
      // Save current selection as the initial remembered set
      persistSelection(selectedMerchants);
    } else {
      // Clear saved selection when toggled off
      clearSavedSelection();
    }
  };

  const handleClearSavedSelection = () => {
    clearSavedSelection();
    toast({ title: "Saved selection cleared", description: "Next wizard open will use defaults." });
  };


  // File upload handlers
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newFlyers: UploadedFlyer[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file.type !== "application/pdf") {
        toast({
          title: "Invalid file type",
          description: `${file.name} is not a PDF file`,
          variant: "destructive",
        });
        continue;
      }

      newFlyers.push({
        id: crypto.randomUUID(),
        file,
        filename: file.name,
        size: file.size,
        uploadedAt: new Date(),
        status: "READY",
        merchantSourceId: null,
        merchantKey: null,
      });
    }

    setUploadedFlyers((prev) => [...prev, ...newFlyers]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleFlyerSourceChange = (flyerId: string, sourceId: string) => {
    const source = flyerSources.find((s) => s.id === sourceId);
    setUploadedFlyers((prev) =>
      prev.map((f) =>
        f.id === flyerId
          ? { ...f, merchantSourceId: sourceId, merchantKey: source?.merchant_id || null }
          : f
      )
    );
  };

  const handleDeleteFlyer = (flyerId: string) => {
    setUploadedFlyers((prev) => prev.filter((f) => f.id !== flyerId));
  };

  const getEnabledSources = () => {
    const sources: string[] = [];
    if (enableFlyers) sources.push("FLYER");
    if (enableWebsite) sources.push("WEBSITE_PROMPT_PACK");
    // CR450: Forum Signals removed entirely
    return sources;
  };

  // CR450: Flyer upload step is only shown when Flyer Scan is ON + capture type = UPLOAD
  const showFlyerUploadStep = enableFlyers && flyerCaptureType === "UPLOAD";

  // CR451: Stepper guard — if user is on flyers-config but capture type changed away from UPLOAD, skip to review
  useEffect(() => {
    if (step === "flyers-config" && !showFlyerUploadStep) {
      setStep("review");
    }
  }, [step, showFlyerUploadStep]);

  // CR451: Clear stale upload/bucket state when capture type changes away from UPLOAD
  useEffect(() => {
    if (flyerCaptureType !== "UPLOAD") {
      setUploadedFlyers([]);
      setSelectedBucketFlyers([]);
      setBucketSourceId(null);
      setFlyerInputMode("bucket");
    }
  }, [flyerCaptureType]);

  // CR451: Validation — merchant scope required for Website OR Flyer+Download/ImageCapture
  const canProceed = () => {
    const isDownloadCapture = enableFlyers && flyerCaptureType === "DOWNLOAD";
    switch (step) {
      case "scope": {
        // Merchants required if Website enabled, or Flyers+Download, or Flyers+ImageCapture
        const merchantRequired =
          enableWebsite ||
          (enableFlyers && flyerCaptureType === "DOWNLOAD") ||
          (enableFlyers && flyerCaptureType === "IMAGE_CAPTURE");
        if (merchantRequired) {
          return selectedMerchants.size > 0;
        }
        return true;
      }
      case "sources": {
        // Each enabled source must have its corresponding pack selected
        const hasSource = enableFlyers || enableWebsite;
        // DOWNLOAD_ONLY runs are intentionally decoupled from OCR+AI scan configuration.
        const flyerPackOk = !enableFlyers || isDownloadCapture || !!flyerPackId;
        const websitePackOk = !enableWebsite || !!websitePackId;
        return hasSource && flyerPackOk && websitePackOk;
      }
      case "flyers-config":
        // CR168: Bucket mode requires selected flyers; Upload mode requires assigned merchant sources
        if (!enableFlyers) return true;
        if (flyerInputMode === "bucket") {
          return selectedBucketFlyers.length > 0;
        } else {
          return uploadedFlyers.length > 0 &&
            uploadedFlyers.every((f) => f.merchantSourceId !== null);
        }
      case "review":
        return true;
      default:
        return true;
    }
  };

  // CR450: Step order: scope → sources → flyers-config (UPLOAD only) → review
  const nextStep = () => {
    switch (step) {
      case "scope":
        setStep("sources");
        break;
      case "sources":
        if (showFlyerUploadStep) {
          setStep("flyers-config");
        } else {
          setStep("review");
        }
        break;
      case "flyers-config":
        setStep("review");
        break;
    }
  };

  const prevStep = () => {
    switch (step) {
      case "sources":
        setStep("scope");
        break;
      case "flyers-config":
        setStep("sources");
        break;
      case "review":
        if (showFlyerUploadStep) {
          setStep("flyers-config");
        } else {
          setStep("sources");
        }
        break;
    }
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      const sources = getEnabledSources();

      // CR075: Verify auth before attempting uploads to private bucket
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({
          title: "Authentication Required",
          description: "Please log in to create a batch run with flyers.",
          variant: "destructive",
        });
        setCreating(false);
        return;
      }

      // Upload flyer PDFs to storage first (Upload mode only)
      // CR168: For Bucket mode, flyers are already in storage - just reference them
      // CR168: Unified FlyerInput schema with explicit {bucket, key}
      const flyerArtifacts: Array<{
        id: string;
        filename: string;
        bucket: string;           // Storage bucket name
        key: string;              // Storage object key (full path)
        merchantSourceId: string | null;
        merchantKey: string;
      }> = [];

      const uploadErrors: string[] = [];

      if (enableFlyers) {
        if (flyerInputMode === "bucket" && selectedBucketFlyers.length > 0) {
          // CR168: Bucket mode - use explicit bucket + key from discovery
          for (const flyer of selectedBucketFlyers) {
            flyerArtifacts.push({
              id: crypto.randomUUID(),
              filename: flyer.filename,
              bucket: flyer.bucket_name,      // Bucket from discovery result
              key: flyer.object_path,         // Full storage object key
              merchantSourceId: null,
              merchantKey: flyer.matched_merchant_id || flyer.merchant_key_normalized,
            });
          }
          console.log(`[CreateBatchRun] Added ${flyerArtifacts.length} bucket flyers with explicit bucket/key`);
        } else if (flyerInputMode === "upload" && uploadedFlyers.length > 0) {
          // Upload mode - upload files to storage
          const validFlyers = uploadedFlyers.filter(
            f => f.status !== "DELETED" && f.merchantSourceId
          );

          if (validFlyers.length === 0) {
            toast({
              title: "No Valid Flyers",
              description: "All flyers are deleted or missing merchant assignment.",
              variant: "destructive",
            });
            setCreating(false);
            return;
          }

          for (const flyer of validFlyers) {
            const storagePath = `batch-run-flyers/${crypto.randomUUID()}/${flyer.filename}`;

            console.log(`[CreateBatchRun] Uploading flyer: ${flyer.filename} to ${storagePath}`);

            const { error: uploadError } = await supabase.storage
              .from("flyer-fetch-assets")
              .upload(storagePath, flyer.file, {
                contentType: "application/pdf",
              });

            if (uploadError) {
              console.error(`[CreateBatchRun] Failed to upload ${flyer.filename}:`, uploadError);
              uploadErrors.push(`${flyer.filename}: ${uploadError.message}`);
              continue;
            }

            console.log(`[CreateBatchRun] Successfully uploaded: ${flyer.filename}`);

            flyerArtifacts.push({
              id: flyer.id,
              filename: flyer.filename,
              bucket: "flyer-fetch-assets",    // Upload mode always uses this bucket
              key: storagePath,                // Full storage key
              merchantSourceId: flyer.merchantSourceId!,
              merchantKey: flyer.merchantKey!,
            });
          }

          // CR075: Abort if ALL flyer uploads failed
          if (flyerArtifacts.length === 0 && validFlyers.length > 0) {
            toast({
              title: "All Flyer Uploads Failed",
              description: `Could not upload any PDFs. Errors: ${uploadErrors.join("; ")}. Check that you have admin permissions.`,
              variant: "destructive",
            });
            setCreating(false);
            return;
          }

          // Warn if some uploads failed
          if (uploadErrors.length > 0) {
            toast({
              title: "Some Uploads Failed",
              description: `${uploadErrors.length} file(s) failed: ${uploadErrors.slice(0, 2).join("; ")}${uploadErrors.length > 2 ? "..." : ""}`,
              variant: "destructive",
            });
          }
        }
      }

      const isImageCaptureRun = enableFlyers && flyerCaptureType === "IMAGE_CAPTURE";
      const phase1AutoEnabled = batchAutoRunPhase1;

      // CR131: Build auto_mode_config from ops_settings defaults
      // Image-capture runs should start at S1 (not S0.5) and only auto-enable S2 when WEB is enabled.
      const autoModeConfig = {
        auto_mode: {
          "S0.5": phase1AutoEnabled && enableFlyers && !isImageCaptureRun,
          S1: phase1AutoEnabled && enableFlyers,
          S2: phase1AutoEnabled && enableWebsite,
          S4: phase1AutoEnabled,
          S5: phase1AutoEnabled,
          S5_CROP: false,
          S6: opsDefaults.auto_publish,
          S6_5: opsDefaults.auto_publish,
          S7: opsDefaults.auto_publish,
        },
        auto_policy: {
          stop_on_failure: true,
          step_timeout_ms: 0,
        },
        auto_state: {
          running_group: null,
          running_substep: null,
          last_triggered_at: null,
          last_error: null,
        },
      };

      // CR404: Store both UUIDs (canonical) and keys (legacy compatibility)
      const selectedMerchantUuids = Array.from(selectedMerchants);
      const selectedMerchantKeys = merchants
        .filter((m) => selectedMerchants.has(m.merchant_uuid))
        .map((m) => m.merchant_key);

      // CR451: Top-level wizard capture type (display + future pipeline CR)
      const flyerCaptureTypeValue: string = flyerCaptureType;
      const isDownloadCapture = enableFlyers && flyerCaptureTypeValue === "DOWNLOAD";

      // CR450: flyerCaptureMode — maps wizard capture type to canonical S0 FlyerCaptureMode enum
      // IMAGE_CAPTURE → 'IMAGE_CAPTURE' (merchant-based adapter pipeline, skips S1.0/S1.1)
      // DOWNLOAD → 'DOWNLOAD_ONLY' (standard PDF download pipeline)
      // UPLOAD → null (upload-based, uses default PDF pipeline)
      const flyerCaptureModeValue: string | null =
        flyerCaptureType === "IMAGE_CAPTURE" ? "IMAGE_CAPTURE" :
          flyerCaptureType === "DOWNLOAD" ? "DOWNLOAD_ONLY" : null;

      const s1AutoChainEnabled = enableFlyers && flyerCaptureType === "IMAGE_CAPTURE" ? s1AutoRun : false;

      const config = {
        // CR404: UUIDs are canonical; keys kept for backward compatibility
        merchant_uuids: selectedMerchantUuids,
        merchants: selectedMerchantKeys, // Legacy - will be phased out
        sources,
        // CR451: Wizard-level capture type persisted at top level for review + future CRs
        flyerCaptureType: enableFlyers ? flyerCaptureTypeValue : undefined,
        // CR131: Include auto_mode_config from ops_settings
        auto_mode_config: autoModeConfig,
        // CR552/CR702: S1 auto-run config (new canonical + legacy fallback)
        s1: enableFlyers ? {
          auto_chain_enabled: s1AutoChainEnabled,
          auto_policy: {
            stop_on_hard_error: true,
          },
          auto_state: {
            status: "IDLE",
            current_step: null,
            next_step: null,
            stop_reason: null,
            stop_step: null,
            last_transition: null,
          },
        } : undefined,
        s1_auto_run: enableFlyers ? s1AutoChainEnabled : undefined,
        // CR451: website block includes webCrawlerProvider run-level override
        website: enableWebsite
          ? {
            prompt_pack_ids: websitePackId ? [websitePackId] : [],
            concurrent: concurrentEnabled,
            max_concurrent: maxConcurrent,
            timeout_seconds: timeoutSeconds,
            // CR451: Run-level web crawler override (matches app_scan_settings enum)
            webCrawlerProvider: webCrawler,
          }
          : null,
        flyers: enableFlyers
          ? {
            mode: flyerInputMode,
            bucket_source_id: flyerInputMode === "bucket" ? bucketSourceId : null,
            artifacts: flyerArtifacts,
            auto_delete_after_extraction: autoDeleteAfterExtraction,
          }
          : null,
        // CR075: Flyer Extraction config snapshot
        // CR229: Use flyer-specific pack
        // CR451: flyerCaptureMode and ocrEngine written here (canonical location)
        flyerExtraction: enableFlyers
          ? {
            ocrEngine: isDownloadCapture ? null : ocrEngine,
            // CR451: Set flyerCaptureMode to DOWNLOAD_ONLY when Download selected;
            // null for UPLOAD (default pipeline) and IMAGE_CAPTURE (stub, deferred).
            flyerCaptureMode: flyerCaptureModeValue,
            aiEnabled: isDownloadCapture ? false : true,
            aiModel: isDownloadCapture ? null : "google/gemini-3-flash-preview",
            maxAiCalls: isDownloadCapture ? 0 : 50,
            candidateThreshold: 12,
            minOcrScore: 25,
            promptPackId: isDownloadCapture ? null : flyerPackId,
            inputMode: flyerInputMode,
            bucketSourceId: flyerInputMode === "bucket" ? bucketSourceId : null,
          }
          : null,
        // CR450: Forum Signals removed entirely
        // CR451: scan_overrides kept for orchestrator-layer reads (snake_case contract)
        scan_overrides: {
          ...(enableFlyers ? {
            flyer_capture_type: flyerCaptureTypeValue,
            flyer_capture_mode: flyerCaptureModeValue,
            ocr_engine: isDownloadCapture ? null : ocrEngine,
          } : {}),
          ...(enableWebsite ? {
            web_crawler: webCrawler,
          } : {}),
        },
      };

      // CR101 + CR102: Resolve dedup mode from selected pack config, then persist snapshot
      let resolvedDedupeMode: string | null = null;
      let resolvedDedupeAiEnabled: boolean | null = null;
      let resolvedDedupeAiProvider: string | null = null;
      let resolvedDedupeAiConfidenceThreshold: number | null = null;

      // Resolve pack config to snapshot dedup mode
      const dedupePackIdToUse = rulePackSelection.dedupePackId;
      if (dedupePackIdToUse) {
        // Fetch pack config to get mode
        const { data: packData } = await supabase
          .from("dedupe_rule_packs")
          .select("config_json")
          .eq("id", dedupePackIdToUse)
          .single();
        if (packData?.config_json) {
          const cfg = packData.config_json as Record<string, unknown>;
          resolvedDedupeMode = (cfg.mode as string) || "deterministic";
          const aiConfig = cfg.ai as Record<string, unknown> | undefined;
          resolvedDedupeAiEnabled = aiConfig?.enabled === true;
          resolvedDedupeAiProvider = (aiConfig?.provider as string) || null;
          resolvedDedupeAiConfidenceThreshold = typeof aiConfig?.confidence_threshold === "number"
            ? aiConfig.confidence_threshold
            : null;
        }
      } else if (rulePackSelection.dedupePackSource === "active") {
        // Resolve from active pack (CR421: single activation, no environment)
        const { data: activationData } = await supabase
          .from("rule_pack_activations")
          .select("active_pack_id")
          .eq("pack_type", "DEDUPE")
          .single();
        if (activationData?.active_pack_id) {
          const { data: packData } = await supabase
            .from("dedupe_rule_packs")
            .select("config_json")
            .eq("id", activationData.active_pack_id)
            .single();
          if (packData?.config_json) {
            const cfg = packData.config_json as Record<string, unknown>;
            resolvedDedupeMode = (cfg.mode as string) || "deterministic";
            const aiConfig = cfg.ai as Record<string, unknown> | undefined;
            resolvedDedupeAiEnabled = aiConfig?.enabled === true;
            resolvedDedupeAiProvider = (aiConfig?.provider as string) || null;
            resolvedDedupeAiConfidenceThreshold = typeof aiConfig?.confidence_threshold === "number"
              ? aiConfig.confidence_threshold
              : null;
          }
        }
      }
      // Fallback to deterministic if nothing resolved
      if (!resolvedDedupeMode) {
        resolvedDedupeMode = "deterministic";
      }

      // CR414: Build step_keys array based on enabled sources
      // CR450: Forum Signals (S3) removed
      const stepKeys: string[] = [];
      if (enableFlyers) stepKeys.push("S1");
      if (enableWebsite) stepKeys.push("S2");
      stepKeys.push("S4", "S5", "S6", "S6_5", "S7", "S8");

      // CR414: Use transactional RPC — run + steps in one atomic operation
      const { data: createdRunId, error: rpcError } = await supabase.rpc(
        "rpc_cr075_create_batch_run",
        {
          p_config_json: config,
          p_sources_enabled: sources,
          p_normalize_rule_pack_id: rulePackSelection.normalizePackId,
          p_normalize_rule_pack_source: rulePackSelection.normalizePackSource,
          p_dedupe_rule_pack_id: rulePackSelection.dedupePackId,
          p_dedupe_rule_pack_source: rulePackSelection.dedupePackSource,
          p_dedupe_mode: resolvedDedupeMode,
          p_dedupe_ai_enabled: resolvedDedupeAiEnabled,
          p_dedupe_ai_provider: resolvedDedupeAiProvider,
          p_dedupe_ai_confidence_threshold: resolvedDedupeAiConfidenceThreshold,
          p_step_keys: stepKeys,
          // CR552/CR702: Seed S1 substep modes based on auto-run toggle (image-capture first)
          p_s1_auto_run: s1AutoChainEnabled,
        }
      );

      if (rpcError) throw rpcError;

      // CR414: Downstream artifact writes — non-fatal (run + steps already exist)
      if (flyerArtifacts.length > 0) {
        const artifactInserts = flyerArtifacts.map((a) => ({
          batch_run_id: createdRunId,
          step_key: "S1",
          artifact_type: "FLYER_PDF",
          artifact_key: a.filename,
          storage_ref: a.key,
          merchant_source_id: a.merchantSourceId,
          metadata_json: {
            merchant_key: a.merchantKey,
            original_filename: a.filename,
            auto_delete: autoDeleteAfterExtraction,
            bucket: a.bucket,
          },
        }));

        const { error: artifactError } = await supabase
          .from("cr075_batch_run_artifacts")
          .insert(artifactInserts);

        if (artifactError) {
          console.error("Failed to create artifact records:", artifactError);
          toast({
            title: "Batch Run Created (with warnings)",
            description: `Run created but flyer artifacts failed: ${artifactError.message}`,
            variant: "default",
          });
          onCreated(createdRunId);
          return;
        }
      }

      toast({
        title: "Batch Run Created",
        description: `Created batch run with ${selectedMerchants.size} merchants`,
      });

      onCreated(createdRunId);
    } catch (err: unknown) {
      // CR414: Surface real Postgres/RLS error details
      console.error("Failed to create batch run:", err);
      const pgError = err as { code?: string; message?: string; details?: string; hint?: string };
      const parts: string[] = [];
      if (pgError.code) parts.push(`[${pgError.code}]`);
      if (pgError.message) parts.push(pgError.message);
      if (pgError.details) parts.push(pgError.details);
      if (pgError.hint) parts.push(`Hint: ${pgError.hint}`);
      toast({
        title: "Failed to create batch run",
        description: parts.length > 0 ? parts.join(" — ") : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setCreating(false);
    }
  };

  const handleClose = () => {
    // CR450: Reset to scope (merchants first)
    setStep("scope");
    setUploadedFlyers([]);
    // CR168: Reset bucket discovery state
    setFlyerInputMode("bucket");
    setBucketSourceId(null);
    setSelectedBucketFlyers([]);
    // CR450: Reset new source config state
    setFlyerCaptureType("UPLOAD");
    setOcrEngine("paddle_ocr");
    setWebCrawler("firecrawl");
    onOpenChange(false);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // CR450: Step indicator — scope → sources → [flyers-config if UPLOAD] → review
  const getStepIndicatorSteps = () => {
    const allSteps: WizardStep[] = ["scope", "sources"];
    if (showFlyerUploadStep) allSteps.push("flyers-config");
    allSteps.push("review");
    return allSteps;
  };

  // CR450: Human-readable step labels for the indicator
  const STEP_LABELS: Record<WizardStep, string> = {
    scope: "Merchants",
    sources: "Sources",
    "flyers-config": "Flyer upload",
    review: "Review",
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[calc(100vh-48px)] flex flex-col p-0 gap-0 overflow-hidden">
        <div className="px-6 pt-6 pb-4 flex-shrink-0">
          <DialogHeader>
            <DialogTitle>Create Batch Run</DialogTitle>
            <DialogDescription>
              Configure and create a new batch run for deal collection
            </DialogDescription>
          </DialogHeader>

          {/* Step indicator */}
          <div className="flex items-center justify-center gap-2 py-3">
            {getStepIndicatorSteps().map((s, i) => {
              const isActive = s === step;
              const isPast = getStepIndicatorSteps().indexOf(step) > i;
              return (
                <div key={s} className="flex items-center gap-2">
                  {i > 0 && <div className="w-8 h-0.5 bg-muted" />}
                  <div className="flex flex-col items-center gap-1">
                    <div
                      className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${isActive
                          ? "bg-primary text-primary-foreground"
                          : isPast
                            ? "bg-primary/20 text-primary"
                            : "bg-muted text-muted-foreground"
                        }`}
                    >
                      {isPast ? <Check className="h-3 w-3" /> : i + 1}
                    </div>
                    <span className={`text-[10px] whitespace-nowrap ${isActive ? "text-foreground font-medium" : "text-muted-foreground"}`}>
                      {STEP_LABELS[s as WizardStep]}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <Separator className="flex-shrink-0" />

        {/* Step content - scrollable body */}
        <ScrollArea className="flex-1 min-h-0">
          <div className="px-6 py-4">
            {step === "scope" && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-medium">Merchants in Scope</Label>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="select-all"
                      checked={selectAll}
                      onCheckedChange={(checked) => handleSelectAll(checked as boolean)}
                    />
                    <Label htmlFor="select-all" className="text-sm">
                      Select All ({merchants.length})
                    </Label>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  * Supports Flyer Scan (has at least 1 active flyer source)
                </p>

                {/* CR551: Remember merchants toggle */}
                <div className="flex items-center justify-between rounded-md border border-dashed px-3 py-2">
                  <div className="flex items-center gap-2">
                    <BookmarkCheck className="h-4 w-4 text-muted-foreground" />
                    <Label htmlFor="remember-merchants" className="text-sm cursor-pointer">
                      Keep last selected merchants
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    {rememberMerchants && loadSavedSelection() && (
                      <Button
                        variant="link"
                        size="sm"
                        className="h-auto p-0 text-xs text-muted-foreground hover:text-destructive"
                        onClick={handleClearSavedSelection}
                      >
                        Clear saved
                      </Button>
                    )}
                    <Switch
                      id="remember-merchants"
                      checked={rememberMerchants}
                      onCheckedChange={handleRememberToggle}
                    />
                  </div>
                </div>
                {loadingMerchants ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <ScrollArea className="h-[300px] border rounded-md p-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3">
                      {/* CR404: Use merchant_uuid as key and for selection */}
                      {merchants.map((m) => (
                        <div key={m.merchant_uuid} className="flex items-center gap-2">
                          <Checkbox
                            id={m.merchant_uuid}
                            checked={selectedMerchants.has(m.merchant_uuid)}
                            onCheckedChange={() => handleMerchantToggle(m.merchant_uuid)}
                          />
                          <Label htmlFor={m.merchant_uuid} className="text-sm truncate">
                            {m.display_name}
                            {flyerSupportedMerchantKeys.has(m.merchant_key) ? "*" : ""}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
                <p className="text-sm text-muted-foreground">
                  {selectedMerchants.size} of {merchants.length} merchants selected
                </p>
              </div>
            )}

            {step === "sources" && (
              <div className="space-y-3">
                <div>
                  <Label className="text-base font-medium">Toggle Sources</Label>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Choose which sources to run and configure scan settings.
                  </p>
                </div>

                {/* CR450 UX: 2-column grid on desktop so both cards fit without scroll */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-start">

                  {/* CR450: Flyer Scan — S1 */}
                  <div className="border rounded-lg overflow-hidden">
                    <div className="flex items-center justify-between p-3">
                      <div className="flex items-center gap-3">
                        <FileText className="h-5 w-5 text-muted-foreground" />
                        <div>
                          <div className="font-medium text-sm">Flyer Scan — S1</div>
                          <div className="text-xs text-muted-foreground">Upload and process flyer PDFs</div>
                        </div>
                      </div>
                      <Switch checked={enableFlyers} onCheckedChange={setEnableFlyers} />
                    </div>

                    {enableFlyers && (
                      <div className="px-3 pb-3 pt-2 border-t bg-muted/30">
                        {/* CR450 UX: Mini vertical stepper — pipeline order: S1.0 → S1.2 → S1.3 */}
                        <div className="relative space-y-0">
                          {/* Vertical connector line */}
                          <div className="absolute left-[11px] top-5 bottom-5 w-px bg-border" />

                          {/* Step 1: Capture type — S1.0 */}
                          <div className="relative flex gap-2.5 pb-3">
                            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center z-10">
                              <span className="text-[9px] font-bold text-primary">1</span>
                            </div>
                            <div className="flex-1 space-y-1 pt-0.5">
                              <div className="flex items-center gap-1.5">
                                <span className="text-xs font-medium">Capture type</span>
                                <span className="text-[9px] text-muted-foreground font-mono bg-muted px-1 py-0 rounded">S1.0</span>
                              </div>
                              <Select
                                value={flyerCaptureType}
                                onValueChange={(v) => setFlyerCaptureType(v as FlyerCaptureType)}
                              >
                                <SelectTrigger className="h-7 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="UPLOAD">Upload</SelectItem>
                                  <SelectItem value="DOWNLOAD">Download</SelectItem>
                                  <SelectItem value="IMAGE_CAPTURE">Image capture</SelectItem>
                                </SelectContent>
                              </Select>
                              <p className="text-[10px] text-muted-foreground leading-tight">
                                {flyerCaptureType === "UPLOAD" && "Manual PDFs → Flyer upload step"}
                                {flyerCaptureType === "DOWNLOAD" && "Auto-download → skips upload step"}
                                {flyerCaptureType === "IMAGE_CAPTURE" && "Image capture → skips upload step"}
                              </p>
                            </div>
                          </div>

                          {flyerCaptureType !== "DOWNLOAD" && (
                            <>
                              {/* Step 2: OCR engine — S1.2 */}
                              <div className="relative flex gap-2.5 pb-3">
                                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center z-10">
                                  <span className="text-[9px] font-bold text-primary">2</span>
                                </div>
                                <div className="flex-1 space-y-1 pt-0.5">
                                  <div className="flex items-center gap-1.5">
                                    <span className="text-xs font-medium">OCR engine</span>
                                    <span className="text-[9px] text-muted-foreground font-mono bg-muted px-1 py-0 rounded">S1.2</span>
                                  </div>
                                  <Select
                                    value={ocrEngine}
                                    onValueChange={(v) => setOcrEngine(v as WizardOcrEngine)}
                                  >
                                    <SelectTrigger className="h-7 text-xs">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {WIZARD_OCR_ENGINES.map(e => (
                                        <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              </div>

                              {/* Step 3: Deep Scan prompt pack — S1.4 */}
                              <div className="relative flex gap-2.5">
                                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center z-10">
                                  <span className="text-[9px] font-bold text-primary">3</span>
                                </div>
                                <div className="flex-1 space-y-1 pt-0.5">
                                  <div className="flex items-center gap-1.5">
                                    <span className="text-xs font-medium">AI prompt pack</span>
                                    <span className="text-[9px] text-muted-foreground font-mono bg-muted px-1 py-0 rounded">S1.4</span>
                                  </div>
                                  {loadingPacks ? (
                                    <div className="flex items-center gap-2 py-1 text-xs text-muted-foreground">
                                      <Loader2 className="h-3 w-3 animate-spin" />
                                      Loading...
                                    </div>
                                  ) : promptPacks.filter(p => packSupports(p, "FLYER")).length === 0 ? (
                                    <div className="space-y-0.5">
                                      <div className="text-xs text-warning flex items-center gap-1">
                                        <AlertCircle className="h-3 w-3" />
                                        No Flyer packs found.
                                      </div>
                                      <p className="text-[10px] text-muted-foreground">
                                        <a href="/admin/prompt-packs" className="underline text-primary">Create one</a> in Prompt Pack Builder.
                                      </p>
                                    </div>
                                  ) : (
                                    <Select
                                      value={flyerPackId || undefined}
                                      onValueChange={(value) => setFlyerPackId(value)}
                                    >
                                      <SelectTrigger className="h-7 text-xs">
                                        <SelectValue placeholder="Select Flyer prompt pack" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {promptPacks.filter(p => packSupports(p, "FLYER")).map((p) => (
                                          <SelectItem key={p.id} value={p.id}>
                                            {p.name || "Untitled pack"} v{p.version}
                                            {p.is_active && (
                                              <Badge variant="outline" className="text-[9px] px-1 py-0 ml-1">Active</Badge>
                                            )}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  )}
                                  {enableFlyers && !flyerPackId && !loadingPacks && promptPacks.filter(p => packSupports(p, "FLYER")).length > 0 && (
                                    <p className="text-[10px] text-destructive flex items-center gap-1">
                                      <AlertCircle className="h-3 w-3" />
                                      Select a pack to continue.
                                    </p>
                                  )}
                                </div>
                              </div>
                            </>
                          )}
                          {flyerCaptureType === "DOWNLOAD" && (
                            <div className="relative flex gap-2.5">
                              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center z-10">
                                <span className="text-[9px] font-bold text-primary">2</span>
                              </div>
                              <div className="flex-1 space-y-1 pt-0.5">
                                <div className="flex items-center gap-1.5">
                                  <span className="text-xs font-medium">Download-only mode</span>
                                </div>
                                <p className="text-[10px] text-muted-foreground leading-tight">
                                  OCR engine and AI prompt configuration are intentionally skipped for this run.
                                </p>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* CR552/CR702: S1 Auto-Run toggle (Image Capture only in this phase) */}
                        {flyerCaptureType === "IMAGE_CAPTURE" && (
                          <div className="flex items-center justify-between pt-2 mt-2 border-t border-border/50">
                            <div>
                              <div className="text-xs font-medium">Enable Auto-Run for S1 Steps</div>
                              <div className="text-[10px] text-muted-foreground">When ON, S1 steps chain automatically. When OFF, each must be started manually.</div>
                            </div>
                            <Switch
                              checked={s1AutoRun}
                              onCheckedChange={setS1AutoRun}
                              className="data-[state=checked]:bg-primary"
                            />
                          </div>
                        )}
                        {flyerCaptureType !== "IMAGE_CAPTURE" && (
                          <div className="pt-2 mt-2 border-t border-border/50">
                            <div className="text-xs font-medium">S1 Auto-Run</div>
                            <div className="text-[10px] text-muted-foreground">
                              Manual-only for this capture type in the current phase. Each S1 step can still be run from the run detail page.
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* CR450: Website Collect — S2 */}
                  <div className="border rounded-lg overflow-hidden">
                    <div className="flex items-center justify-between p-3">
                      <div className="flex items-center gap-3">
                        <Globe className="h-5 w-5 text-muted-foreground" />
                        <div>
                          <div className="font-medium text-sm">Website Collect — S2</div>
                          <div className="text-xs text-muted-foreground">Execute prompt packs against merchant websites</div>
                        </div>
                      </div>
                      <Switch checked={enableWebsite} onCheckedChange={setEnableWebsite} />
                    </div>
                    {enableWebsite && (
                      <div className="px-3 pb-3 pt-2 border-t bg-muted/30">
                        {/* CR450 UX: Mini vertical stepper — pipeline order: S2.0 → S2.1 */}
                        <div className="relative space-y-0">
                          {/* Vertical connector line */}
                          <div className="absolute left-[11px] top-5 bottom-5 w-px bg-border" />

                          {/* Step 1: Web crawler — S2.0 */}
                          <div className="relative flex gap-2.5 pb-3">
                            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center z-10">
                              <span className="text-[9px] font-bold text-primary">1</span>
                            </div>
                            <div className="flex-1 space-y-1 pt-0.5">
                              <div className="flex items-center gap-1.5">
                                <span className="text-xs font-medium">Web crawler</span>
                                <span className="text-[9px] text-muted-foreground font-mono bg-muted px-1 py-0 rounded">S2.0</span>
                              </div>
                              <Select
                                value={webCrawler}
                                onValueChange={(v) => setWebCrawler(v as WebCrawlerOption)}
                              >
                                <SelectTrigger className="h-7 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {WEB_CRAWLER_OPTIONS.map(c => (
                                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              {webCrawler === "playwright" && (
                                <p className="text-[10px] text-muted-foreground leading-tight">
                                  Not yet deployed — falls back to Firecrawl at runtime.
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Step 2: AI prompt pack — S2.1 */}
                          <div className="relative flex gap-2.5">
                            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center z-10">
                              <span className="text-[9px] font-bold text-primary">2</span>
                            </div>
                            <div className="flex-1 space-y-1 pt-0.5">
                              <div className="flex items-center gap-1.5">
                                <span className="text-xs font-medium">AI prompt pack</span>
                                <span className="text-[9px] text-muted-foreground font-mono bg-muted px-1 py-0 rounded">S2.1</span>
                              </div>
                              {loadingPacks ? (
                                <div className="flex items-center gap-2 py-1 text-xs text-muted-foreground">
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                  Loading...
                                </div>
                              ) : promptPacks.filter(p => packSupports(p, "WEBSITE")).length === 0 ? (
                                <div className="space-y-0.5">
                                  <div className="text-xs text-warning flex items-center gap-1">
                                    <AlertCircle className="h-3.5 w-3.5" />
                                    No Website packs found.
                                  </div>
                                  <p className="text-[10px] text-muted-foreground">
                                    <a href="/admin/prompt-packs" className="underline text-primary">Create one</a> in Prompt Pack Builder.
                                  </p>
                                </div>
                              ) : (
                                <Select
                                  value={websitePackId || undefined}
                                  onValueChange={(value) => setWebsitePackId(value)}
                                >
                                  <SelectTrigger className="w-full h-7 text-xs">
                                    <SelectValue placeholder="Select a Website prompt pack" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {promptPacks.filter(p => packSupports(p, "WEBSITE")).map((p) => (
                                      <SelectItem key={p.id} value={p.id}>
                                        {p.name || "Untitled pack"} v{p.version}
                                        {p.is_active && (
                                          <Badge variant="outline" className="text-[9px] px-1 py-0 ml-1">Active</Badge>
                                        )}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              )}
                              {enableWebsite && !websitePackId && !loadingPacks && promptPacks.filter(p => packSupports(p, "WEBSITE")).length > 0 && (
                                <p className="text-[10px] text-destructive flex items-center gap-1">
                                  <AlertCircle className="h-3 w-3" />
                                  Select a pack to continue.
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* CR450: Forum Signals tile fully removed */}
                </div>
              </div>
            )}

            {/* CR403: Rule packs step removed - defaults from S0 config */}

            {step === "flyers-config" && (
              <div className="space-y-4">
                <div>
                  <Label className="text-base font-medium">Flyer upload</Label>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Select flyers from the bucket or upload PDFs directly.
                  </p>
                </div>
                {/* CR168: Mode selector with bucket discovery or upload */}
                <FlyerInputModeSelector
                  mode={flyerInputMode}
                  onModeChange={setFlyerInputMode}
                  bucketSourceId={bucketSourceId}
                  onBucketSourceChange={setBucketSourceId}
                  selectedFlyers={selectedBucketFlyers}
                  onSelectedFlyersChange={setSelectedBucketFlyers}
                >
                  {/* Upload mode content (passed as children) */}
                  <div className="space-y-4">
                    {/* Dropzone */}
                    <div
                      className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                      <p className="text-sm font-medium">Click to upload PDFs</p>
                      <p className="text-xs text-muted-foreground">or drag and drop</p>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="application/pdf"
                        multiple
                        className="hidden"
                        onChange={handleFileSelect}
                      />
                    </div>

                    {/* Uploaded files list */}
                    {uploadedFlyers.length > 0 && (
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">
                          Uploaded Files ({uploadedFlyers.length})
                        </Label>
                        <ScrollArea className="h-[200px] border rounded-md">
                          <div className="p-3 space-y-2">
                            {uploadedFlyers.map((flyer) => (
                              <div
                                key={flyer.id}
                                className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg"
                              >
                                <FileText className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium truncate">{flyer.filename}</div>
                                  <div className="text-xs text-muted-foreground">
                                    {formatFileSize(flyer.size)}
                                  </div>
                                </div>
                                <Select
                                  value={flyer.merchantSourceId || ""}
                                  onValueChange={(v) => handleFlyerSourceChange(flyer.id, v)}
                                >
                                  <SelectTrigger className="w-[200px]">
                                    <SelectValue placeholder="Select merchant..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {loadingFlyerSources ? (
                                      <div className="p-2 text-center text-sm text-muted-foreground">
                                        Loading...
                                      </div>
                                    ) : flyerSources.length === 0 ? (
                                      <div className="p-2 text-center text-sm text-muted-foreground">
                                        No flyer sources found
                                      </div>
                                    ) : (
                                      flyerSources.map((source) => (
                                        <SelectItem key={source.id} value={source.id}>
                                          {source.merchant_name || source.merchant_id}
                                        </SelectItem>
                                      ))
                                    )}
                                  </SelectContent>
                                </Select>
                                {!flyer.merchantSourceId && (
                                  <AlertCircle className="h-4 w-4 text-warning flex-shrink-0" />
                                )}
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleDeleteFlyer(flyer.id)}
                                >
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        </ScrollArea>
                        {uploadedFlyers.some((f) => !f.merchantSourceId) && (
                          <p className="text-sm text-warning flex items-center gap-1">
                            <AlertCircle className="h-4 w-4" />
                            All flyers must have a merchant source assigned
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </FlyerInputModeSelector>

                <Separator />

                {/* Auto-delete setting */}
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="auto-delete" className="text-sm font-medium">
                      Auto-delete PDFs after extraction
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Delete PDF files after successfully extracting deals (keeps staged deals)
                    </p>
                  </div>
                  <Switch
                    id="auto-delete"
                    checked={autoDeleteAfterExtraction}
                    onCheckedChange={setAutoDeleteAfterExtraction}
                  />
                </div>
              </div>
            )}

            {/* CR403: Website config step removed - execution settings now in S0 Scan Config */}

            {step === "review" && (
              <div className="space-y-4">
                <Label className="text-base font-medium">Review Configuration</Label>
                {enableFlyers && flyerCaptureType === "DOWNLOAD" && (
                  <div className="p-3 border rounded-lg space-y-1.5 bg-muted/20">
                    <div className="font-medium text-sm">Download Intent</div>
                    <div className="text-xs text-muted-foreground">
                      This run will only execute flyer download/manifest collection.
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Merchants selected: <span className="font-medium text-foreground">{selectedMerchants.size}</span>
                    </div>
                  </div>
                )}

                <div className="space-y-3">
                  {/* Scope */}
                  <div className="p-3 border rounded-lg space-y-1.5">
                    <div className="font-medium text-sm">Scope</div>
                    <div className="text-sm text-muted-foreground">
                      {selectedMerchants.size} of {merchants.length} merchants selected
                    </div>
                  </div>

                  {/* Sources summary */}
                  <div className="p-3 border rounded-lg space-y-2">
                    <div className="font-medium text-sm">Sources</div>
                    <div className="flex flex-wrap gap-2">
                      {enableFlyers && <Badge variant="outline" className="text-xs">Flyer Scan — S1</Badge>}
                      {enableWebsite && <Badge variant="outline" className="text-xs">Website Collect — S2</Badge>}
                      {!enableFlyers && !enableWebsite && <span className="text-xs text-muted-foreground">No sources enabled</span>}
                    </div>
                    {enableFlyers && flyerCaptureType !== "DOWNLOAD" && (() => {
                      const flyerPack = promptPacks.find(p => p.id === flyerPackId);
                      return <div className="text-xs text-muted-foreground">Flyer pack: {flyerPack ? `${flyerPack.name} v${flyerPack.version}` : "None"}</div>;
                    })()}
                    {enableWebsite && (() => {
                      const websitePack = promptPacks.find(p => p.id === websitePackId);
                      return <div className="text-xs text-muted-foreground">Website pack: {websitePack ? `${websitePack.name} v${websitePack.version}` : "None"}</div>;
                    })()}
                  </div>

                  {/* Flyers detail */}
                  {enableFlyers && (
                    <div className="p-3 border rounded-lg space-y-1.5">
                      <div className="font-medium text-sm">Flyers</div>
                      <div className="text-xs text-muted-foreground">
                        Capture type: <span className="font-medium text-foreground">{FLYER_CAPTURE_TYPE_LABELS[flyerCaptureType]}</span>
                      </div>
                      {flyerCaptureType !== "DOWNLOAD" && (
                        <div className="text-xs text-muted-foreground">
                          OCR engine: <span className="font-medium text-foreground">{WIZARD_OCR_ENGINES.find(e => e.value === ocrEngine)?.label ?? ocrEngine}</span>
                        </div>
                      )}
                      {flyerCaptureType === "DOWNLOAD" && (
                        <div className="text-xs text-muted-foreground">
                          Intent: <span className="font-medium text-foreground">Download flyers only (no OCR/AI in this run)</span>
                        </div>
                      )}
                      {flyerCaptureType === "UPLOAD" && (
                        <>
                          <div className="text-xs text-muted-foreground">
                            Upload mode: <span className="font-medium text-foreground">{flyerInputMode === "bucket" ? "Bucket (auto-discover)" : "Upload PDFs"}</span>
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {flyerInputMode === "bucket"
                              ? `${selectedBucketFlyers.length} flyers selected from bucket`
                              : `${uploadedFlyers.length} PDFs staged for upload`}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            Auto-delete after extraction: <span className="font-medium text-foreground">{autoDeleteAfterExtraction ? "Yes" : "No"}</span>
                          </div>
                        </>
                      )}
                      {/* CR450: Image capture info callout */}
                      {flyerCaptureType === "IMAGE_CAPTURE" && (
                        <div className="mt-1 flex items-start gap-1.5 text-[11px] text-muted-foreground bg-muted/40 border border-border rounded px-2 py-1.5">
                          <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 mt-px text-primary" />
                          <span>
                            Image capture mode uses merchant sources with adapters configured in Image Capture Studio.
                            S1.0 Collect and S1.1 Render will be skipped — captured pages feed directly to S1.2 OCR.
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Website detail */}
                  {enableWebsite && (
                    <div className="p-3 border rounded-lg space-y-1.5">
                      <div className="font-medium text-sm">Website</div>
                      <div className="text-xs text-muted-foreground">
                        Web crawler: <span className="font-medium text-foreground">{WEB_CRAWLER_OPTIONS.find(c => c.value === webCrawler)?.label ?? webCrawler}</span>
                      </div>
                      {webCrawler === "playwright" && (
                        <div className="text-xs text-warning">
                          ⚠ Playwright will fall back to Firecrawl at runtime.
                        </div>
                      )}
                    </div>
                  )}

                  {/* Rule Packs */}
                  <div className="p-3 border rounded-lg space-y-1.5">
                    <div className="font-medium text-sm">Rule Packs</div>
                    <div className="text-xs text-muted-foreground">
                      Normalize: {rulePackSelection.normalizePackSource === "run_override" ? "Override (selected pack)" : "Use Active Pack"}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Dedup: {rulePackSelection.dedupePackSource === "run_override" ? "Override (selected pack)" : "Use Active Pack"}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        <Separator className="flex-shrink-0" />

        <DialogFooter className="px-6 py-4 flex-shrink-0 justify-between">
          <div className="flex items-center">
            {step === "sources" && (
              <div className="flex items-center gap-2">
                <Label htmlFor="batch-auto-run-phase1" className="text-xs text-muted-foreground">
                  Enable Batch Auto-Run (S1→S2→S4→S5)
                </Label>
                <Switch
                  id="batch-auto-run-phase1"
                  checked={batchAutoRunPhase1}
                  onCheckedChange={setBatchAutoRunPhase1}
                />
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {step !== "scope" && (
              <Button variant="outline" onClick={prevStep} disabled={creating}>
                <ChevronLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
            )}
            {step !== "review" ? (
              <Button onClick={nextStep} disabled={!canProceed()}>
                Next
                <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <Button onClick={handleCreate} disabled={creating}>
                {creating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {enableFlyers && flyerCaptureType === "DOWNLOAD" ? "Starting download..." : "Creating..."}
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    {enableFlyers && flyerCaptureType === "DOWNLOAD" ? "Start download" : "Create Batch Run"}
                  </>
                )}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
